import { createElement, useEffect, useReducer, useRef, useState, type JSX } from "react";
import { flushSync } from "react-dom";
import { ChevronRight, Menu, X } from "lucide-react";
import {
  registryChangedEvent,
  type AppMountContext,
  type NotificationPushPayload,
  type RegistryChangedPayload
} from "@tc-superpods/app-contract";
import type { UserManager } from "oidc-client-ts";
import {
  createAnonymousIdentity,
  createResolvedTenantContext,
  createIdentityFromUser,
  createUserManager,
  isAuthCallbackPath,
  resolveAuthenticatedUser,
  triggerReauthentication,
  type SetAuth
} from "./auth";
import {
  authMode,
  mode,
  sidebarCollapsedStorageKey
} from "./config";
import { applyStoredThemeConfig } from "./theme";
import { createDialogController, type DialogController } from "./dialog-controller";
import { createNotificationController, type NotificationController } from "./notifications";
import { orderTenants, readRecentTenantIds, recordRecentTenant } from "./recent-tenants";
import {
  getCurrentAppPath,
  appendLocationSuffix,
  loadSelectedTenantRuntime,
  loadTenantBootstrap,
  parseRequestedRoute,
  refreshSelectedTenantRuntime,
  isPreviewSession,
  resolveDestination,
  resolveWorkspaceContext,
  toBrowserPath,
  invalidateTenantRegistry
} from "./registry";
import { createRouter, type Router } from "./router";
import { createInitialState, getDebugNavItems, shellReducer } from "./state";
import type { LoadedTenantRuntime, NavEntry, PreviewSession, RegistryEntry, TenantSummary, Workspace } from "./types";
import { AuthScreen } from "./components/AuthScreen";
import { LoadingStateScreen } from "./components/LoadingStateScreen";
import { ContentArea } from "./components/ContentArea";
import { NotificationRegion } from "./components/NotificationRegion";
import { AppShell } from "./chrome/AppShell";
import { Sidebar } from "./chrome/Sidebar";
import { NavIcon } from "./chrome/NavIcon";
import { useIsMobile } from "./chrome/hooks/use-is-mobile";

type TenantSelectionMode = "interactive" | "auto";

export interface ShellAppProps {
  rootElement: HTMLElement;
}

export function ShellApp({ rootElement }: ShellAppProps): JSX.Element {
  const [state, dispatch] = useReducer(shellReducer, undefined, createInitialState);
  const isMobile = useIsMobile();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [tenantSearch, setTenantSearch] = useState("");

  useEffect(() => {
    if (!isMobile) {
      setMobileNavOpen(false);
    }
  }, [isMobile]);

  const outletRef = useRef<HTMLElement>(null);
  const busRef = useRef<AppMountContext["bus"] | null>(null);
  const userManagerRef = useRef<UserManager | null>(null);
  const reauthInFlightRef = useRef(false);

  useEffect(() => {
    const originalPushState = window.history.pushState.bind(window.history);
    const pushState: History["pushState"] = (...args) => {
      originalPushState(...args);
      outletRef.current?.scrollTo({ top: 0 });
    };

    window.history.pushState = pushState;

    return () => {
      if (window.history.pushState === pushState) {
        window.history.pushState = originalPushState;
      }
    };
  }, []);
  const identityRef = useRef<AppMountContext["identity"] | null>(null);
  const tenantRef = useRef<AppMountContext["tenant"] | null>(null);
  const accessibleTenantsRef = useRef<TenantSummary[]>([]);
  const registryRef = useRef<RegistryEntry[]>([]);
  const workspacesRef = useRef<Workspace[]>([]);
  const variablesRef = useRef<Record<string, string>>({});
  const activeWorkspaceIdRef = useRef<string | null>(null);
  const authStatusRef = useRef(state.auth.status);
  const routerStartedRef = useRef(false);
  const registryRefreshInFlightRef = useRef(false);
  const shellTimelineStartRef = useRef(performance.now());
  const lastLoggedShellRenderStateRef = useRef("");

  authStatusRef.current = state.auth.status;

  const dialogControllerRef = useRef<DialogController | null>(null);
  const dialogController = (dialogControllerRef.current ??= createDialogController(dispatch));

  const notificationsRef = useRef<NotificationController | null>(null);
  const notifications = (notificationsRef.current ??= createNotificationController(dispatch));

  const logShellTimeline = (step: string, details?: Record<string, unknown>): void => {
    if (mode === "production") {
      return;
    }

    const elapsedMs =
      Math.round((performance.now() - shellTimelineStartRef.current) * 10) / 10;

    console.info("[shell timeline]", {
      ...(details ?? {}),
      elapsedMs,
      step
    });
  };

  const routerRef = useRef<Router | null>(null);
  const showNoAppsRef = useRef<() => Promise<void>>(async () => {});
  const router = (routerRef.current ??= createRouter({
    dispatch,
    getOutlet: () => outletRef.current,
    getSession: () => {
      const bus = busRef.current;
      const identity = identityRef.current;
      const tenant = tenantRef.current;

      if (!bus || !identity || !tenant) {
        throw new Error("Platform session was not initialized.");
      }

      return { bus, identity, tenant };
    },
    getAccessibleTenants: () => accessibleTenantsRef.current,
    getBrowserPath: (route, workspaceId) =>
      toBrowserPath(
        route,
        tenantRef.current?.id ?? null,
        accessibleTenantsRef.current,
        workspaceId === undefined ? activeWorkspaceIdRef.current : workspaceId
      ),
    getRegistry: () => registryRef.current,
    getWorkspaces: () => workspacesRef.current,
    getVariables: () => variablesRef.current,
    getActiveWorkspaceId: () => activeWorkspaceIdRef.current,
    onWorkspaceResolved: (workspaceId) => {
      if (activeWorkspaceIdRef.current === workspaceId) {
        return;
      }
      activeWorkspaceIdRef.current = workspaceId;
      dispatch({ type: "set-active-workspace", workspaceId });
    },
    onNoApps: () => showNoAppsRef.current(),
    dialogController,
    log: logShellTimeline
  }));

  const setAuth: SetAuth = (status, message) => {
    dispatch({ type: "set-auth", status, message });
  };

  const showTenantSelection = (requestedAppPath: string, preview?: PreviewSession): void => {
    setTenantSearch("");
    const selection = {
      message: "Choose a tenant to continue.",
      loadingTenantId: null,
      requestedAppPath,
      preview
    } as const;

    dispatch({ type: "set-tenant-selection", selection });
    dispatch({ type: "set-screen", screen: "tenant-selection" });
  };

  const showAccessDenied = async (title: string, message: string): Promise<void> => {
    await router.clearActiveApp();
    tenantRef.current = null;
    registryRef.current = [];
    workspacesRef.current = [];
    variablesRef.current = {};
    activeWorkspaceIdRef.current = null;
    dispatch({ type: "set-current-entry", entry: null });
    dispatch({ type: "set-registry", registry: [] });
    dispatch({ type: "set-workspaces", workspaces: [] });
    dispatch({ type: "set-active-workspace", workspaceId: null });
    dispatch({
      type: "set-access-denied",
      accessDenied: {
        title,
        message
      }
    });
    dispatch({ type: "set-screen", screen: "access-denied" });
  };

  const showNoApps = async (): Promise<void> => {
    tenantRef.current = null;
    registryRef.current = [];
    workspacesRef.current = [];
    variablesRef.current = {};
    activeWorkspaceIdRef.current = null;
    dispatch({ type: "set-current-entry", entry: null });
    dispatch({ type: "set-registry", registry: [] });
    dispatch({ type: "set-workspaces", workspaces: [] });
    dispatch({ type: "set-active-workspace", workspaceId: null });
    dispatch({
      type: "set-no-apps",
      noApps: {
        title: "No applications available",
        message: "This tenant does not have an application assigned to its first workspace."
      }
    });
    dispatch({ type: "set-screen", screen: "no-apps" });
  };
  showNoAppsRef.current = showNoApps;

  const applyLoadedTenantRuntime = async (
    runtime: LoadedTenantRuntime,
    selectionMode: TenantSelectionMode
  ): Promise<void> => {
    try {
      logShellTimeline("tenant-selection:start", {
        requestedAppPath: runtime.requestedAppPath,
        selectionMode,
        tenantId: runtime.tenant.id
      });

      const selection = {
        message: `Loading ${runtime.tenant.name}.`,
        loadingTenantId: runtime.tenant.id,
        requestedAppPath: runtime.requestedAppPath
      } as const;
      const loading = {
        title: "Loading tenant",
        message: `Loading ${runtime.tenant.name}.`
      } as const;

      flushSync(() => {
        dispatch({ type: "set-tenant-selection", selection });
        dispatch({ type: "set-loading", loading });
        dispatch({
          type: "set-screen",
          screen: selectionMode === "auto" ? "loading" : "tenant-selection"
        });
      });

      if (selectionMode === "auto") {
        await waitForNextPaint();
      }

      await router.clearActiveApp();
      logShellTimeline("tenant-selection:previous-app-unmounted", {
        tenantId: runtime.tenant.id
      });

      registryRef.current = runtime.registry;
      workspacesRef.current = runtime.workspaces;
      variablesRef.current = runtime.variables;
      dispatch({ type: "set-workspaces", workspaces: runtime.workspaces });
      tenantRef.current = createResolvedTenantContext(
        runtime.tenant,
        runtime.registry,
        null,
        runtime.tenantToken
      );
      accessibleTenantsRef.current = accessibleTenantsRef.current.length === 0
        ? [runtime.tenant]
        : accessibleTenantsRef.current;

      logShellTimeline("tenant-selection:registry-ready", {
        appIds: runtime.registry.map((entry) => entry.id),
        tenantId: runtime.tenant.id
      });
      const resetSelection = {
        message: "Choose a tenant to continue.",
        loadingTenantId: null,
        requestedAppPath: runtime.requestedAppPath
      } as const;
      const resolvedDestination = resolveDestination(
        runtime.tenant.id,
        runtime.requestedAppPath,
        runtime.registry,
        runtime.workspaces
      );

      if (!resolvedDestination.path) {
        await showNoApps();
        return;
      }

      logShellTimeline("tenant-selection:destination-resolved", {
        destination: resolvedDestination.path,
        tenantId: runtime.tenant.id
      });

      flushSync(() => {
        dispatch({ type: "set-registry", registry: runtime.registry });
        dispatch({ type: "set-tenant-selection", selection: resetSelection });
        dispatch({ type: "set-screen", screen: "app" });
      });

      const destination = resolvedDestination.preserveLocationState
        ? appendLocationSuffix(resolvedDestination.path, window.location.search, window.location.hash)
        : resolvedDestination.path;
      if (destination !== getRequestedPath()) {
        window.history.replaceState(window.history.state, "", destination);
      }

      if (!routerStartedRef.current) {
        routerStartedRef.current = true;
        await router.start();
      } else {
        await router.handlePopState();
      }

      logShellTimeline("tenant-selection:complete", {
        tenantId: runtime.tenant.id
      });
    } catch (error) {
      dispatch({
        type: "set-tenant-selection",
        selection: {
          message: "Choose a tenant to continue.",
          loadingTenantId: null,
          requestedAppPath: runtime.requestedAppPath
        }
      });
      await showAccessDenied(
        "Tenant selection failed",
        error instanceof Error ? error.message : "Tenant selection failed."
      );
    }
  };

  const selectTenant = async (
    selectedTenant: TenantSummary,
    requestedAppPath: string,
    selectionMode: TenantSelectionMode = "interactive",
    preview?: PreviewSession
  ): Promise<void> => {
    const identity = identityRef.current;
    if (!identity) {
      throw new Error("Platform identity was not initialized.");
    }

    if (selectionMode === "interactive") {
      dispatch({
        type: "set-tenant-selection",
        selection: {
          message: `Loading ${selectedTenant.name}.`,
          loadingTenantId: selectedTenant.id,
          requestedAppPath,
          preview
        }
      });
    }

    const runtime = await loadSelectedTenantRuntime(
      selectedTenant,
      requestedAppPath,
      identity,
      logShellTimeline,
      preview
    );
    recordRecentTenant(identity.userId, selectedTenant.id);
    await applyLoadedTenantRuntime(runtime, selectionMode);
  };

  const initializeTenantExperience = async (): Promise<void> => {
    const identity = identityRef.current;
    if (!identity) {
      throw new Error("Platform identity was not initialized.");
    }

    if (shouldShowLoadingDuringBootstrap(window.location.pathname)) {
      dispatch({ type: "set-screen", screen: "loading" });
    }

    logShellTimeline("tenant-experience:start", {
      path: window.location.pathname
    });
    const bootstrapResult = await loadTenantBootstrap(
      window.location.pathname,
      identity,
      logShellTimeline
    );

    if (bootstrapResult.kind === "access-denied") {
      await showAccessDenied(bootstrapResult.title, bootstrapResult.message);
      return;
    }

    if (bootstrapResult.kind === "selection-required") {
      accessibleTenantsRef.current = bootstrapResult.tenants;
      dispatch({ type: "set-accessible-tenants", tenants: bootstrapResult.tenants });
      logShellTimeline("tenant-experience:tenants-loaded", {
        tenantIds: bootstrapResult.tenants.map((tenant) => tenant.id)
      });
      showTenantSelection(bootstrapResult.requestedAppPath, bootstrapResult.preview);
      return;
    }

    accessibleTenantsRef.current = bootstrapResult.tenants;
    dispatch({ type: "set-accessible-tenants", tenants: bootstrapResult.tenants });
    logShellTimeline("tenant-experience:tenants-loaded", {
      tenantIds: bootstrapResult.tenants.map((tenant) => tenant.id)
    });
    await selectTenant(bootstrapResult.tenant, bootstrapResult.requestedAppPath, "auto", bootstrapResult.preview);
  };

  const applyRegistry = (registry: RegistryEntry[]): void => {
    registryRef.current = registry;
    dispatch({ type: "set-registry", registry });
  };

  const handleRegistryChanged = async (payload: RegistryChangedPayload): Promise<void> => {
    if (isPreviewSession()) {
      return;
    }

    const tenant = tenantRef.current;
    const identity = identityRef.current;

    if (!tenant || !identity || tenant.id !== payload.tenantId) {
      return;
    }

    const optimisticEntry = payload.entry;
    if (optimisticEntry) {
      applyRegistry(mergeRegistryEntry(registryRef.current, optimisticEntry));
    }

    if (registryRefreshInFlightRef.current) {
      return;
    }

    registryRefreshInFlightRef.current = true;
    try {
      const runtime = await refreshSelectedTenantRuntime(
        tenant,
        getCurrentAppPath(window.location.pathname, accessibleTenantsRef.current),
        identity,
        logShellTimeline
      );
      if (tenantRef.current?.id !== tenant.id || identityRef.current !== identity) return;

      applyRegistry(
        optimisticEntry ? mergeRegistryEntry(runtime.registry, optimisticEntry) : runtime.registry
      );
      workspacesRef.current = runtime.workspaces;
      variablesRef.current = runtime.variables;
      dispatch({ type: "set-workspaces", workspaces: runtime.workspaces });
      logShellTimeline("registry-refresh:applied", {
        appIds: registryRef.current.map((entry) => entry.id),
        workspaceIds: runtime.workspaces.map((workspace) => workspace.id),
        tenantId: tenant.id
      });
    } catch (error) {
      logShellTimeline("registry-refresh:failed", {
        message: error instanceof Error ? error.message : String(error),
        tenantId: tenant.id
      });
    } finally {
      registryRefreshInFlightRef.current = false;
    }
  };

  const handleLocationChange = async (): Promise<void> => {
    logShellTimeline("navigation:location-change", {
      path: window.location.pathname
    });

    if (authStatusRef.current !== "authenticated") {
      return;
    }

    const tenants = accessibleTenantsRef.current;
    if (tenants.length === 0) {
      return;
    }

    const requestedRoute = parseRequestedRoute(window.location.pathname, tenants);

    if (requestedRoute.kind === "invalid-tenant") {
      await showAccessDenied(
        "Access denied",
        `Tenant ${requestedRoute.tenantId} is not available for your account.`
      );
      return;
    }

    if (requestedRoute.kind === "tenant-qualified") {
      if (!tenantRef.current || tenantRef.current.id !== requestedRoute.tenant.id) {
        await selectTenant(requestedRoute.tenant, requestedRoute.appPath, "auto");
        return;
      }

      dispatch({ type: "set-screen", screen: "app" });
      await router.handlePopState();
      return;
    }

    if (!tenantRef.current) {
      showTenantSelection(requestedRoute.appPath);
      return;
    }

    const resolvedDestination = resolveDestination(
      tenantRef.current.id,
      requestedRoute.appPath,
      registryRef.current,
      workspacesRef.current
    );

    if (resolvedDestination.path !== null) {
      const destination = resolvedDestination.preserveLocationState
        ? appendLocationSuffix(resolvedDestination.path, window.location.search, window.location.hash)
        : resolvedDestination.path;
      if (destination !== getRequestedPath()) {
        window.history.replaceState(window.history.state, "", destination);
      }
    }

    dispatch({ type: "set-screen", screen: "app" });
    await router.handlePopState();
  };

  useEffect(() => {
    const cleanupFns: Array<() => void> = [];
    let cancelled = false;

    const handleKeyDown = (event: KeyboardEvent): void => {
      dialogController.handleEscape(event);
    };
    const handlePopState = (): void => {
      void handleLocationChange();
    };
    const requestReauth = (): void => {
      const manager = userManagerRef.current;
      if (manager && !reauthInFlightRef.current) {
        reauthInFlightRef.current = true;
        void triggerReauthentication(manager, setAuth);
      }
    };

    async function bootstrap(): Promise<void> {
      try {
        logShellTimeline("bootstrap:start");
        dispatch({ type: "shell-ready" });
        dispatch({ type: "set-screen", screen: "auth" });
        if (isAuthCallbackPath(window.location.pathname)) {
          setAuth("callback", "Completing sign-in.");
        } else {
          setAuth("loading", "Checking your session.");
        }

        const { createPlatformBus } = await import("@tc-superpods/platform-bus");
        const bus = createPlatformBus();
        busRef.current = bus;

        if (mode !== "production") {
          window.__knBus = bus;
        }

        cleanupFns.push(
          bus.on<NavEntry>("nav:register", (entry) => {
            dispatch({ type: "nav-register", entry });
          }),
          bus.on<Pick<NavEntry, "id">>("nav:unregister", ({ id }) => {
            dispatch({ type: "nav-unregister", id });
          }),
          bus.on<NotificationPushPayload>("notification:push", (payload) => {
            notifications.push(payload);
          }),
          bus.on<RegistryChangedPayload>(registryChangedEvent, (payload) => {
            void handleRegistryChanged(payload);
          })
        );

        window.addEventListener("keydown", handleKeyDown);
        window.addEventListener("popstate", handlePopState);

        let identity: AppMountContext["identity"];

        if (authMode === "none") {
          logShellTimeline("bootstrap:auth-disabled");
          identity = createAnonymousIdentity();
        } else {
          const userManager = createUserManager();
          userManagerRef.current = userManager;
          userManager.events.addAccessTokenExpired(requestReauth);
          userManager.events.addSilentRenewError(requestReauth);

          await userManager.clearStaleState();
          logShellTimeline("bootstrap:stale-state-cleared");

          const authenticatedUser = await resolveAuthenticatedUser(userManager, setAuth);
          if (!authenticatedUser || cancelled) {
            return;
          }

          identity = createIdentityFromUser(userManager, authenticatedUser, requestReauth);
        }

        if (cancelled) {
          return;
        }

        identityRef.current = identity;
        dispatch({ type: "set-identity-email", email: identity.email });
        logShellTimeline("bootstrap:identity-created", {
          userId: identity.userId
        });
        flushSync(() => {
          dispatch({ type: "set-auth", status: "authenticated", message: "Authenticated." });
        });
        await initializeTenantExperience();
        logShellTimeline("bootstrap:complete");
      } catch (error) {
        const message = error instanceof Error ? error.message : "Shell startup failed.";
        dispatch({ type: "shell-ready" });
        setAuth("error", message);
      }
    }

    void bootstrap();

    return () => {
      cancelled = true;
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("popstate", handlePopState);
      for (const cleanup of cleanupFns) {
        cleanup();
      }
    };
  }, []);

  useEffect(() => {
    dialogController.mountPending();
  }, [state.dialogs, dialogController]);

  useEffect(() => {
    applyStoredThemeConfig();
  }, []);

  useEffect(() => {
    rootElement.dataset.navItems = JSON.stringify(getDebugNavItems(state));
    rootElement.dataset.sidebarCollapsed = String(state.sidebarCollapsed);
    rootElement.hidden = !state.shellReady;
  }, [rootElement, state]);

  useEffect(() => {
    if (mode === "production") {
      return;
    }

    const renderState = JSON.stringify({
      authStatus: state.auth.status,
      currentStatus: state.currentEntry ? "mounted" : "not-loaded",
      currentTenantId: tenantRef.current?.id ?? null,
      shellReady: state.shellReady,
      shellScreen: state.screen
    });

    if (renderState === lastLoggedShellRenderStateRef.current) {
      return;
    }

    lastLoggedShellRenderStateRef.current = renderState;
    logShellTimeline("render-shell", JSON.parse(renderState) as Record<string, unknown>);
  }, [state]);

  const handleSignOut = (): void => {
    const manager = userManagerRef.current;
    if (!manager) {
      if (authMode === "none") {
        return;
      }

      throw new Error("User manager was not initialized.");
    }

    void (async () => {
      await manager.removeUser();
      await triggerReauthentication(manager, setAuth);
    })();
  };

  const handleChangeTenant = (): void => {
    const requestedAppPath = getCurrentAppPath(window.location.pathname, accessibleTenantsRef.current);

    const currentTenantId = tenantRef.current?.id;
    if (currentTenantId) {
      invalidateTenantRegistry(currentTenantId);
    }
    tenantRef.current = null;
    registryRef.current = [];
    workspacesRef.current = [];
    variablesRef.current = {};
    activeWorkspaceIdRef.current = null;
    setMobileNavOpen(false);
    dispatch({ type: "set-current-entry", entry: null });
    dispatch({ type: "set-registry", registry: [] });
    dispatch({ type: "set-workspaces", workspaces: [] });
    dispatch({ type: "set-active-workspace", workspaceId: null });
    dispatch({ type: "set-expanded-app", appId: null });
    // The picker owns the visible screen immediately; the queued unmount clears
    // the previous remote app and its registered navigation entries.
    void router.clearActiveApp();
    showTenantSelection(requestedAppPath);
  };

  const setSidebarCollapsed = (collapsed: boolean): void => {
    localStorage.setItem(sidebarCollapsedStorageKey, String(collapsed));
    dispatch({ type: "toggle-sidebar", collapsed });
  };

  const toggleSidebar = (): void => {
    if (isMobile) {
      setMobileNavOpen((open) => !open);
      return;
    }

    setSidebarCollapsed(!state.sidebarCollapsed);
  };

  const handleWorkspaceChange = (workspaceId: string): void => {
    if (workspaceId === activeWorkspaceIdRef.current) {
      return;
    }

    activeWorkspaceIdRef.current = workspaceId;
    dispatch({ type: "set-active-workspace", workspaceId });

    const workspace = workspacesRef.current.find((entry) => entry.id === workspaceId);
    const destinationApp = workspace?.apps[0];

    if (destinationApp) {
      router.navigateTo(destinationApp.route);
    }
  };

  const handleExpandedAppChange = (appId: string | null): void => {
    dispatch({ type: "set-expanded-app", appId });
  };

  const isAuthenticated = state.auth.status === "authenticated";
  const currentEntry = state.currentEntry;
  const rawAppPath = getCurrentAppPath(window.location.pathname, state.accessibleTenants);
  // rawAppPath still carries the workspace-id segment (if any) ahead of the app's
  // own route - strip it so this matches the plain entry.route values nav items
  // are keyed by, or every nav item's active-highlight check fails.
  const currentAppPath = resolveWorkspaceContext(
    rawAppPath,
    state.workspaces,
    state.registry,
    state.activeWorkspaceId
  ).appPath;
  const orderedTenants = orderTenants(
    state.accessibleTenants,
    identityRef.current ? readRecentTenantIds(identityRef.current.userId) : []
  );
  const useHybridTenantSelection = state.accessibleTenants.length > 5;
  const normalizedTenantSearch = tenantSearch.trim().toLocaleLowerCase();
  const filterTenants = (tenants: ReadonlyArray<TenantSummary>): TenantSummary[] =>
    normalizedTenantSearch === ""
      ? [...tenants]
      : tenants.filter(
          (tenant) =>
            tenant.name.toLocaleLowerCase().includes(normalizedTenantSearch) ||
            tenant.id.toLocaleLowerCase().includes(normalizedTenantSearch)
        );
  const isTenantSearchActive = normalizedTenantSearch !== "";
  const visibleRecentTenants = orderedTenants.recent;
  const visibleOtherTenants = filterTenants(
    isTenantSearchActive
      ? [...orderedTenants.recent, ...orderedTenants.other]
      : orderedTenants.other
  );
  const renderTenantGroup = (
    group: "recent" | "other",
    label: string,
    tenants: ReadonlyArray<TenantSummary>,
    showEmpty = false
  ): JSX.Element | null => {
    if (tenants.length === 0 && !showEmpty) {
      return null;
    }

    return (
      <section
        aria-labelledby={`platform-tenant-group-${group}`}
        data-platform-tenant-group={group}
      >
        <h2 data-platform-tenant-group-label="" id={`platform-tenant-group-${group}`}>
          {label}
        </h2>
        {(() => { if (tenants.length > 0) return (
          <ul data-platform-tenant-list="">
            {tenants.map((tenantOption) => {
              const isLoading = state.tenantSelection.loadingTenantId === tenantOption.id;

              return (
                <li key={tenantOption.id}>
                  <button
                    type="button"
                    data-platform-tenant-option=""
                    data-tenant-id={tenantOption.id}
                    disabled={state.tenantSelection.loadingTenantId !== null}
                    onClick={() => {
                      void selectTenant(
                        tenantOption,
                        state.tenantSelection.requestedAppPath,
                        "interactive",
                        state.tenantSelection.preview
                      );
                    }}
                  >
                    <span data-platform-tenant-name="">{tenantOption.name}</span>
                    <span data-platform-tenant-status="">
                      {isLoading
                        ? createElement("tc-spinner", {
                            "aria-label": `Loading ${tenantOption.name}`,
                            "data-platform-tenant-spinner": ""
                          })
                        : <ChevronRight className="tenant-chevron" aria-hidden="true" />}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        );
        if (showEmpty) return (
          <p data-platform-tenant-empty="">Your recent tenants will appear here.</p>
        );
        return null;
      })()}
      </section>
    );
  };
  const tenantSelectionHeader = (
    <div data-platform-selection-heading="">
      <h1 data-platform-selection-title="">Select a tenant</h1>
      <p data-platform-selection-body="">{state.tenantSelection.message}</p>
    </div>
  );
  const tenantSelectionList = (
    <div data-platform-tenant-groups="">
      {useHybridTenantSelection ? (
        <>
          {renderTenantGroup("recent", "Recent tenants", visibleRecentTenants, true)}
          <section
            aria-labelledby="platform-tenant-group-other"
            data-platform-tenant-group="other"
          >
            <h2 data-platform-tenant-group-label="" id="platform-tenant-group-other">
              All tenants
            </h2>
            {createElement("tc-input", {
              "aria-label": "Search all tenants by name or ID",
              "data-platform-tenant-search": "",
              placeholder: "Search all tenants",
              type: "search",
              value: tenantSearch,
              onInput: (event: Event) => {
                const target = event.currentTarget;
                if (target instanceof HTMLElement && "value" in target && typeof target.value === "string") {
                  setTenantSearch(target.value);
                }
              }
            })}
            {(() => { if (visibleOtherTenants.length > 0) return (
              <ul data-platform-tenant-list="">
                {visibleOtherTenants.map((tenantOption) => {
                  const isLoading = state.tenantSelection.loadingTenantId === tenantOption.id;

                  return (
                    <li key={tenantOption.id}>
                      <button
                        type="button"
                        data-platform-tenant-option=""
                        data-tenant-id={tenantOption.id}
                        disabled={state.tenantSelection.loadingTenantId !== null}
                        onClick={() => {
                          void selectTenant(
                            tenantOption,
                            state.tenantSelection.requestedAppPath,
                            "interactive",
                            state.tenantSelection.preview
                          );
                        }}
                      >
                        <span data-platform-tenant-name="">{tenantOption.name}</span>
                        <span data-platform-tenant-status="">
                          {isLoading
                            ? createElement("tc-spinner", {
                                "aria-label": `Loading ${tenantOption.name}`,
                                "data-platform-tenant-spinner": ""
                              })
                            : <ChevronRight className="tenant-chevron" aria-hidden="true" />}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            );
            if (isTenantSearchActive) return (
              createElement(
                "tc-empty",
                { "data-platform-tenant-search-empty": "" },
                createElement("h2", { slot: "title" }, "No matching tenants"),
                createElement("p", { slot: "description" }, "Try another tenant name or ID.")
              )
            );
            return null;
          })()}
          </section>
        </>
      ) : (
        renderTenantGroup("other", "All tenants", visibleOtherTenants)
      )}
      {visibleRecentTenants.length === 0 && visibleOtherTenants.length === 0 && !useHybridTenantSelection ? (
        <p data-platform-tenant-empty="">No tenants match your search.</p>
      ) : null}
    </div>
  );
  return (
    <>
      <AppShell
        mobileHeader={
          isMobile && state.screen === "app" && isAuthenticated ? (
            <header
              data-platform-mobile-header=""
              data-platform-chrome=""
              className="flex shrink-0 items-center justify-between px-2"
            >
              <div data-platform-mobile-identity="" className="inline-flex h-full min-w-0 items-center gap-2 px-0">
                {/* The inline colour matches the title below so a currentColor
                    glyph paints in the app's own colour. */}
                <span
                  data-platform-mobile-icon=""
                  aria-hidden="true"
                  className="flex size-6 shrink-0 items-center justify-center text-lg"
                  style={currentEntry?.color ? { color: currentEntry.color } : undefined}
                >
                  <NavIcon icon={currentEntry?.icon} className="h-5 w-5 object-contain" />
                </span>
                <h1
                  data-platform-mobile-title=""
                  className="truncate font-brand text-xl leading-none tracking-wide uppercase"
                  style={currentEntry?.color ? { color: currentEntry.color } : undefined}
                >
                  {currentEntry?.navLabel ?? currentEntry?.id ?? "KoreNext"}
                </h1>
              </div>
              <button
                type="button"
                aria-label={mobileNavOpen ? "Close navigation" : "Open navigation"}
                aria-expanded={mobileNavOpen}
                data-platform-mobile-menu-toggle=""
                onClick={() => setMobileNavOpen((open) => !open)}
                className="flex size-10 items-center justify-center rounded-lg border border-border bg-surface text-text shadow-card transition-[background-color,border-color,transform] duration-150 ease-out hover:border-sidebar-text-muted hover:bg-surface-muted hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none motion-reduce:hover:scale-100 motion-reduce:active:scale-100"
              >
                <span className="flex size-5 items-center justify-center" aria-hidden="true">
                  {mobileNavOpen ? <X className="size-5" strokeWidth={1.75} /> : <Menu className="size-5" strokeWidth={1.75} />}
                </span>
              </button>
            </header>
          ) : null
        }
        sidebar={
          <Sidebar
            isMobile={isMobile}
            isAuthenticated={isAuthenticated}
            authEnabled={authMode === "oidc"}
            showAppChrome={isAuthenticated && state.screen === "app"}
            apps={state.registry}
            currentEntryId={currentEntry?.id ?? null}
            sections={state.navItems}
            activePathname={currentAppPath}
            identityEmail={state.identityEmail}
            tenantName={tenantRef.current?.name ?? null}
            collapsed={isMobile ? false : state.sidebarCollapsed}
            mobileNavOpen={mobileNavOpen}
            workspaces={state.workspaces}
            activeWorkspaceId={state.activeWorkspaceId}
            expandedAppId={state.expandedAppId}
            canChangeTenant={state.accessibleTenants.length > 1}
            resolveHref={(route) =>
              toBrowserPath(route, tenantRef.current?.id ?? null, accessibleTenantsRef.current, activeWorkspaceIdRef.current)
            }
            onNavigate={router.navigateTo}
            onToggleCollapsed={toggleSidebar}
            onMobileClose={() => setMobileNavOpen(false)}
            onWorkspaceChange={handleWorkspaceChange}
            onExpandedAppChange={handleExpandedAppChange}
            onChangeTenant={handleChangeTenant}
            onSignOut={handleSignOut}
          />
        }
      >
      {(() => {
        if (!isAuthenticated) return (
        <AuthScreen status={state.auth.status} message={state.auth.message} />
      );
        if (state.screen === "loading") return (
        <LoadingStateScreen
          stateAttribute="data-platform-loading-state"
          title={state.loading.title}
          message={state.loading.message}
        />
      );
        if (state.screen === "tenant-selection") return (
        <main data-platform-tenant-selection="" data-platform-tenant-selection-mode={useHybridTenantSelection ? "hybrid" : "spacious"}>
          <section data-platform-selection-content="">
            {tenantSelectionHeader}
            {tenantSelectionList}
          </section>
        </main>
      );
        if (state.screen === "access-denied") return (
        <main data-platform-access-denied="">
          <section data-platform-access-denied-card="" role="alert">
            <h1 data-platform-access-denied-title="">{state.accessDenied.title}</h1>
            <p data-platform-access-denied-body="">{state.accessDenied.message}</p>
          </section>
        </main>
      );
        if (state.screen === "no-apps") return (
        <main data-platform-no-apps="">
          <section data-platform-no-apps-card="" role="alert">
            <h1 data-platform-no-apps-title="">{state.noApps.title}</h1>
            <p data-platform-no-apps-body="">{state.noApps.message}</p>
          </section>
        </main>
      );
        return (
        <ContentArea
          title={currentEntry ? currentEntry.navLabel ?? currentEntry.id : null}
          showTopbar={currentEntry?.showTopbar ?? false}
          outletRef={outletRef}
          dialogs={state.dialogs}
          dialogController={dialogController}
        />
      ); })()}
      </AppShell>
      <NotificationRegion notifications={state.notifications} onDismiss={notifications.dismiss} />
    </>
  );
}

function mergeRegistryEntry(registry: RegistryEntry[], entry: RegistryEntry): RegistryEntry[] {
  return registry.some((existing) => existing.id === entry.id) ? registry : [...registry, entry];
}

function getRequestedPath(): string {
  const { pathname, search, hash } = window.location;

  return `${pathname}${search}${hash}`;
}

function waitForNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => resolve());
  });
}

function shouldShowLoadingDuringBootstrap(pathname: string): boolean {
  const normalized = pathname.startsWith("/") ? pathname : `/${pathname}`;
  const segments = normalized.split("/").filter(Boolean);

  return segments.length > 1;
}
